import React from 'react'
import {IContentBlock} from './types'
import {parseStyleToken, styleTokenToClassName} from './utils'

interface IProps {
    blocks: IContentBlock[]
    imageClassName?: string
}

// ── container 레이아웃 → tailwind class 매핑 ────────────────────────────────────
const GAP_CLASS: Record<string, string> = {
    none: 'gap-0',
    xs: 'gap-[4px]',
    sm: 'gap-[8px]',
    md: 'gap-[12px]',
    lg: 'gap-[16px]',
}
const ALIGN_ITEMS: Record<string, string> = {start: 'items-start', center: 'items-center', end: 'items-end'}
const ALIGN_TEXT: Record<string, string> = {start: 'text-left', center: 'text-center', end: 'text-right'}
const JUSTIFY_CLASS: Record<string, string> = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
    evenly: 'justify-evenly',
}
// card 배경색(온도/의미) — 옅은 톤으로만 표현. 모르는 이름은 neutral로 대체.
const BACKGROUND_CLASS: Record<string, string> = {
    neutral: 'bg-[#f5f6f8]',
    skyblue: 'bg-[#eef6ff]',
    positive: 'bg-[#eafaf1]',
    orange: 'bg-[#fff4ea]',
    negative: 'bg-[#fdeeee]',
}
const TAG_CLASS: Record<string, string> = {
    primary: 'bg-primary text-white',
    secondary: 'bg-[#eef6ff] text-sub_blue',
    tertiary: 'bg-[#f0f1f4] text-font02',
    negative: 'bg-[#fdeeee] text-font_red',
}
const SEGMENT_COLOR: Record<string, string> = {positive: 'bg-primary', negative: 'bg-font_red', accent: 'bg-primary'}
const SEGMENT_TEXT: Record<string, string> = {
    positive: 'text-primary',
    negative: 'text-font_red',
    accent: 'text-primary',
}

const containerClassName = (block: IContentBlock): string => {
    const classes: string[] = ['flex']
    classes.push(block.direction === 'row' ? 'flex-row' : 'flex-col')
    const defaultGap = block.direction === 'row' ? 'sm' : 'md'
    classes.push(GAP_CLASS[block.gap || defaultGap] || GAP_CLASS[defaultGap])
    if (block.align) {
        classes.push(ALIGN_ITEMS[block.align] || '')
        classes.push(ALIGN_TEXT[block.align] || '')
    }
    if (block.justify) classes.push(JUSTIFY_CLASS[block.justify] || '')
    // variant가 카드/타일이면 박스(패딩/모서리/테두리)로 그린다. tile은 background를 무시(고정 밝은 배경).
    if (block.variant === 'card') {
        classes.push('rounded-[10px] p-[16px] border border-line02')
        classes.push(BACKGROUND_CLASS[block.background || 'neutral'] || BACKGROUND_CLASS.neutral)
    } else if (block.variant === 'tile') {
        classes.push('rounded-[8px] p-[12px] border border-line02 bg-white')
    }
    if (block.url) classes.push('cursor-pointer')
    // flex:"grow" — 부모 flex 안에서 남은 공간을 채우도록 grow(형제들에 모두 주면 균등 분배 효과).
    if (block.flex === 'grow') classes.push('grow min-w-0')
    return classes.join(' ')
}

const ChartPreview: React.FC<{block: IContentBlock}> = ({block}) => {
    const segments = block.segments || []
    if (block.variant === 'donut') {
        return (
            <div className={'w-full my-[6px]'}>
                {block.text && <div className={'text-[13px] text-font02 mb-[6px]'}>{block.text}</div>}
                <div className={'space-y-[4px]'}>
                    {segments.map(segment => (
                        <div key={segment.id} className={'flex items-center gap-x-[8px] text-[13px]'}>
                            <span
                                className={`inline-block w-[10px] h-[10px] rounded-full ${
                                    SEGMENT_COLOR[segment.style || ''] || 'bg-line02'
                                }`}
                            />
                            <span className={'text-font02'}>{segment.label}</span>
                            <span className={`ml-auto font-bold ${SEGMENT_TEXT[segment.style || ''] || 'text-font01'}`}>
                                {segment.percent}%
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        )
    }
    // bar
    return (
        <div className={'w-full my-[6px] space-y-[6px]'}>
            {segments.map(segment => (
                <div key={segment.id}>
                    <div className={'flex justify-between text-[12px] text-font02 mb-[2px]'}>
                        <span>{segment.label}</span>
                    </div>
                    <div className={'w-full h-[8px] rounded-full bg-[#eef0f3] overflow-hidden'}>
                        <div
                            className={`h-full rounded-full ${SEGMENT_COLOR[segment.style || ''] || 'bg-line02'}`}
                            style={{width: `${Math.max(0, Math.min(100, segment.percent))}%`}}
                        />
                    </div>
                </div>
            ))}
        </div>
    )
}

// 인라인 흐름 타입(paragraph/button/button_mo)은 하나의 텍스트 wrapper로 묶어 렌더한다(아래 renderList 참고).
const isInlineFlow = (type: string): boolean => type === 'paragraph' || type === 'button' || type === 'button_mo'

// 인라인 흐름 조각 하나를 span으로 렌더. paragraph는 style 그대로, button은 수정 전과 동일하게
// cursor-pointer + (block 토큰 없으면 pl-1 간격) + style을 입힌 인라인 텍스트로 렌더한다.
// button_mo는 형제에 button이 있으면 스킵(웹 버전 우선), 단독이면 button과 동일하게 렌더한다.
const renderInline = (block: IContentBlock, hasWebButton: boolean): React.ReactNode => {
    if (block.type === 'paragraph') {
        return (
            <span key={block.id} className={styleTokenToClassName(block.style)}>
                {block.text}
            </span>
        )
    }
    // button / button_mo
    if (block.type === 'button_mo' && hasWebButton) return null
    if (!block.text) return null
    return (
        <span
            key={block.id}
            className={`cursor-pointer ${parseStyleToken(block.style).block ? '' : 'pl-1'} ${styleTokenToClassName(
                block.style,
            )}`}>
            {block.text}
        </span>
    )
}

// paragraph/button 외 단일 블록(container/chart/tag/icon/image)을 렌더. 렌더 안전 규칙에 걸리면 null.
const renderBlock = (block: IContentBlock, imageClassName: string): React.ReactNode => {
    switch (block.type) {
        case 'container': {
            if (!block.blocks?.length) return null
            const children = renderList(block.blocks, imageClassName, block.direction || 'column')
            return (
                <div key={block.id} className={containerClassName(block)}>
                    {children}
                </div>
            )
        }
        case 'chart':
            if (!block.segments?.length) return null
            return <ChartPreview key={block.id} block={block} />
        case 'tag':
            if (!block.text) return null
            return (
                <span
                    key={block.id}
                    className={`inline-flex items-center px-[8px] py-[2px] rounded-full text-[12px] font-bold ${
                        TAG_CLASS[block.variant || 'tertiary'] || TAG_CLASS.tertiary
                    }`}>
                    {block.text}
                </span>
            )
        case 'icon':
            // icon은 미리보기에서 렌더하지 않는다(요청).
            return null
        case 'image':
            if (!block.image) return null
            return (
                <img
                    key={block.id}
                    src={`${block.image}?type=webp&width=1000&quality=100`}
                    alt={''}
                    className={imageClassName}
                />
            )
        default:
            // 모르는/미구현 타입 — 어드민 미리보기에서는 확인용 플레이스홀더를 보여준다.
            return (
                <span key={block.id} className={'text-font03 text-[12px]'}>
                    {` [${block.type}] `}
                </span>
            )
    }
}

// 연속된 인라인 흐름 조각(paragraph/button/button_mo)은 하나의 텍스트 흐름(wrapper)으로 묶어
// "기본 이어붙기, block 토큰만 줄바꿈"이 되게 한다. (container를 flex로 그리면 각 자식이 별도 라인이
// 되어 모든 조각이 줄바꿈되는 문제 방지). paragraph 외 블록은 container flex 레이아웃의 개별 아이템으로 렌더한다.
const renderList = (blocks: IContentBlock[], imageClassName: string, direction: string): React.ReactNode[] => {
    const list = blocks || []
    const hasWebButton = list.some(block => block.type === 'button')
    const nodes: {key: string; node: React.ReactNode}[] = []
    let i = 0
    while (i < list.length) {
        if (isInlineFlow(list[i].type)) {
            let j = i
            const run: IContentBlock[] = []
            while (j < list.length && isInlineFlow(list[j].type)) {
                run.push(list[j])
                j++
            }
            const spans = run.map(block => renderInline(block, hasWebButton)).filter(node => node !== null)
            i = j
            if (!spans.length) continue
            // row에서는 텍스트 흐름을 내용 폭으로, 그 외(column/최상위)에서는 전체 폭으로 둬서 줄바꿈/정렬이 자연스럽게.
            const widthClass = direction === 'row' ? '' : 'w-full'
            nodes.push({
                key: run[0].id,
                node: (
                    <div key={run[0].id} className={`leading-[1.8] break-words min-w-0 ${widthClass}`}>
                        {spans}
                    </div>
                ),
            })
        } else {
            const node = renderBlock(list[i], imageClassName)
            if (node !== null) nodes.push({key: list[i].id, node})
            i++
        }
    }
    return nodes.map(n => <React.Fragment key={n.key}>{n.node}</React.Fragment>)
}

const ContentBlocksInlinePreview: React.FC<IProps> = ({
    blocks,
    imageClassName = 'block max-w-full my-[8px] rounded-[6px]',
}) => {
    return (
        <div className={'flex flex-col gap-[12px]'}>
            {renderList(blocks || [], imageClassName, 'column')}
        </div>
    )
}

export default ContentBlocksInlinePreview
